import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../../utils/config/config";
import { ALERTS, POLLING, URL_PARAMS } from "./PaymentSetup.constants";

export function usePaymentAccount() {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [supportedCountries, setSupportedCountries] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [pollingMessage, setPollingMessage] = useState(null);
  const pollIntervalRef = useRef(null);
  const pollCountRef = useRef(0);

  const fetchAccountStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axiosInstance.get("connect/account-status");
      if (data.success && data.data) {
        setAccountStatus(data.data);
        return data.data;
      }
    } catch (err) {
      setError(
        err.response?.data?.message || ALERTS.LOAD_ACCOUNT_FAILED
      );
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  const fetchPayoutSummary = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("connect/payout-summary");
      if (data.success && data.data) {
        setPayoutSummary(data.data);
      }
    } catch (err) {
      console.error("Failed to load payout summary:", err.message);
    }
  }, []);

  const fetchSupportedCountries = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("connect/supported-countries");
      if (data.success && data.data) {
        setSupportedCountries(data.data);
      }
    } catch (err) {
      console.error("Failed to load supported countries:", err.message);
    }
  }, []);

  useEffect(() => {
    fetchAccountStatus();
    fetchSupportedCountries();
  }, [fetchAccountStatus, fetchSupportedCountries]);

  useEffect(() => {
    if (accountStatus?.onboardingComplete) {
      fetchPayoutSummary();
    }
  }, [accountStatus?.onboardingComplete, fetchPayoutSummary]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const onboardingComplete = params.get(URL_PARAMS.ONBOARDING_COMPLETE);
    const stripeRefresh = params.get(URL_PARAMS.STRIPE_REFRESH);

    if (onboardingComplete === URL_PARAMS.ONBOARDING_COMPLETE_VALUE) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      setPollingMessage(ALERTS.POLLING_VERIFYING);
      pollCountRef.current = 0;

      pollIntervalRef.current = setInterval(async () => {
        pollCountRef.current += 1;
        const status = await fetchAccountStatus();

        if (status?.onboardingComplete || pollCountRef.current >= POLLING.MAX_ATTEMPTS) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPollingMessage(null);

          if (status?.onboardingComplete) {
            setSuccessMessage(ALERTS.SETUP_COMPLETE);
          } else if (status?.detailsSubmitted && !status?.onboardingComplete) {
            setSuccessMessage(null);
          }
        }
      }, POLLING.INTERVAL_MS);
    } else if (stripeRefresh === URL_PARAMS.STRIPE_REFRESH_VALUE) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      setError(ALERTS.STRIPE_REFRESH_EXPIRED);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateAccount = useCallback(async (country) => {
    try {
      setActionLoading(true);
      setError(null);

      const { data: createData } = await axiosInstance.post(
        "connect/create-account",
        { country }
      );
      if (!createData.success) {
        throw new Error(createData.message);
      }

      const { data: linkData } = await axiosInstance.post(
        "connect/onboarding-link"
      );
      if (!linkData.success) {
        throw new Error(linkData.message);
      }

      window.open(linkData.data.url, "_blank");
      setSuccessMessage(ALERTS.ONBOARDING_OPENED_FIRST_TIME);
      setTimeout(() => fetchAccountStatus(), 3000);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || ALERTS.CREATE_ACCOUNT_FAILED
      );
    } finally {
      setActionLoading(false);
    }
  }, [fetchAccountStatus]);

  const handleContinueOnboarding = useCallback(async () => {
    try {
      setActionLoading(true);
      setError(null);

      const { data } = await axiosInstance.post("connect/onboarding-link");
      if (!data.success) {
        throw new Error(data.message);
      }

      window.open(data.data.url, "_blank");
      setSuccessMessage(ALERTS.ONBOARDING_OPENED_NEW_TAB);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          ALERTS.ONBOARDING_LINK_FAILED
      );
    } finally {
      setActionLoading(false);
    }
  }, []);

  const handleOpenDashboard = useCallback(async () => {
    try {
      setActionLoading(true);
      setError(null);

      const { data } = await axiosInstance.post("connect/dashboard-link");
      if (!data.success) {
        throw new Error(data.message);
      }

      window.open(data.data.url, "_blank");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          ALERTS.DASHBOARD_LINK_FAILED
      );
    } finally {
      setActionLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccessMessage(null), []);

  return {
    loading,
    actionLoading,
    accountStatus,
    payoutSummary,
    supportedCountries,
    error,
    successMessage,
    pollingMessage,
    fetchAccountStatus,
    handleCreateAccount,
    handleContinueOnboarding,
    handleOpenDashboard,
    clearError,
    clearSuccess,
  };
}
